import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Leaf } from './Leaf';
import { generateLeafPositions } from './Leaf.helpers';
import { TREE_COLORS, type TreeStage } from './useTreeGrowth';

/** Typed branch segment for clearer geometry */
interface BranchSegment {
  startY: number;
  length: number;
  angle: number;
  delay: number;
  endX: number;
  endY: number;
}

/**
 * Compute branch endpoint from trunk position and angle
 */
function computeBranchEndpoint(
  trunkX: number,
  startY: number,
  length: number,
  angleDegrees: number
): { endX: number; endY: number } {
  const angleRad = (angleDegrees * Math.PI) / 180;
  return {
    endX: trunkX + Math.cos(angleRad) * length,
    endY: startY - Math.sin(Math.abs(angleRad)) * length,
  };
}

interface EvolvingTreeProps {
  /** Current growth stage */
  stage: TreeStage;
  /** Number of leaves to render */
  leafCount: number;
  /** Trunk scale factor (0.5-1.0) */
  trunkScale: number;
  /** Canopy scale factor (0.4-1.0) */
  canopyScale: number;
}

/**
 * SVG tree that evolves based on CO2 level
 * Features animated trunk growth and staggered leaf appearance
 */
export function EvolvingTree({ stage, leafCount, trunkScale, canopyScale }: EvolvingTreeProps) {
  // SVG viewBox dimensions
  const width = 200;
  const height = 240;

  // Tree positioning
  const trunkX = width / 2;
  const groundY = height - 20;
  const trunkHeight = 80 * trunkScale + 40; // Base height + scale
  const trunkWidth = 8 + 12 * trunkScale;
  const trunkTop = groundY - trunkHeight;

  // Canopy center
  const canopyCenterX = trunkX;
  const canopyCenterY = trunkTop - 20;
  const canopyRadius = 50 * canopyScale + 20;

  // Generate leaf positions
  const leafPositions = useMemo(() =>
    generateLeafPositions(leafCount, canopyCenterX, canopyCenterY, canopyRadius),
    [leafCount, canopyCenterX, canopyCenterY, canopyRadius]
  );

  // Branch configuration based on stage
  const branches = useMemo((): BranchSegment[] => {
    const branchCount = stage === 'lush' ? 4 : stage === 'mature' ? 3 : stage === 'young' ? 2 : 1;
    const branchData: BranchSegment[] = [];

    for (let i = 0; i < branchCount; i++) {
      const yOffset = (trunkHeight * 0.3) + (i * (trunkHeight * 0.2));
      const side = i % 2 === 0 ? 1 : -1;
      const length = 20 + (trunkScale * 15);
      const angle = side * (30 + i * 10);
      const startY = groundY - yOffset;
      const { endX, endY } = computeBranchEndpoint(trunkX, startY, length, angle);

      branchData.push({
        startY,
        length,
        angle,
        delay: 0.2 + i * 0.15,
        endX,
        endY,
      });
    }

    return branchData;
  }, [stage, trunkScale, trunkHeight, groundY, trunkX]);

  return (
    <motion.svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-auto max-h-[160px]"
      initial="hidden"
      animate="visible"
    >
      {/* Ground shadow */}
      <motion.ellipse
        cx={trunkX}
        cy={groundY + 5}
        rx={30 * canopyScale}
        ry={6}
        fill="rgba(0,0,0,0.2)"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      />

      {/* Trunk */}
      <motion.rect
        x={trunkX - trunkWidth / 2}
        y={trunkTop}
        width={trunkWidth}
        height={trunkHeight}
        rx={trunkWidth / 4}
        fill={TREE_COLORS.trunk}
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        style={{ originY: 1, transformBox: 'fill-box' }}
        transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15,
          delay: 0,
        }}
      />

      {/* Trunk shadow detail */}
      <motion.rect
        x={trunkX - trunkWidth / 6}
        y={trunkTop}
        width={trunkWidth / 3}
        height={trunkHeight}
        rx={trunkWidth / 6}
        fill={TREE_COLORS.trunkShadow}
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        style={{ originY: 1, transformBox: 'fill-box' }}
        transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15,
          delay: 0.05,
        }}
      />

      {/* Branches */}
      {branches.map((branch, i) => (
        <motion.line
          key={i}
          x1={trunkX}
          y1={branch.startY}
          x2={branch.endX}
          y2={branch.endY}
          stroke={TREE_COLORS.trunk}
          strokeWidth={3 * trunkScale}
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            duration: 0.4,
            delay: branch.delay,
            ease: 'easeOut',
          }}
        />
      ))}

      {/* Canopy container with gentle sway animation */}
      <motion.g
        animate={{
          rotate: [0, 1, 0, -1, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={{ transformOrigin: `${trunkX}px ${trunkTop}px` }}
      >
        {/* Leaves */}
        {leafPositions.map((leaf, i) => (
          <Leaf
            key={i}
            cx={leaf.cx}
            cy={leaf.cy}
            size={leaf.size}
            rotation={leaf.rotation}
            variant={leaf.variant}
            delay={0.4 + i * 0.025}
          />
        ))}
      </motion.g>
    </motion.svg>
  );
}
